/* import __COLOCATED_TEMPLATE__ from './automation-rate-section.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type AppService from 'embercom/services/app-service';
import { tracked } from '@glimmer/tracking';
import type { ParticipatedFilter } from 'embercom/components/reporting/bespoke/fin-performance/types';

interface Signature {
  Args: {
    reportState: ReportState;
    participatedFilter?: ParticipatedFilter;
    participationRateMetricId?: string;
    automationRateMetricId?: string;
    resolutionRateMetricId?: string;
  };
}

export default class AutomationRateSection extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare appService: AppService;

  @tracked showStandaloneSideDrawer = false;

  get participatedFilter(): ParticipatedFilter | null {
    return this.args.participatedFilter ?? null;
  }

  get participationRateMetricId(): string {
    return this.args.participationRateMetricId ?? 'fin.participation_rate';
  }

  get automationRateMetricId(): string {
    return this.args.automationRateMetricId ?? 'fin.automation_rate';
  }

  get resolutionRateMetricId(): string {
    return this.args.resolutionRateMetricId ?? 'fin.resolution_rate';
  }

  get ctaLabel(): string | undefined {
    return this.intl.t(
      'components.reporting.bespoke.fin-performance.automation-rate-section.cta-label-recommendations',
    );
  }

  get ctaTooltip(): string | undefined {
    return this.intl.t(
      'components.reporting.bespoke.fin-performance.automation-rate-section.cta-tooltip-recommendations',
    );
  }

  get automationObjects() {
    return {
      numerator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-resolved',
        ),
        barColorClass: 'bg-vis-mint-40',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-resolved-conversations',
        ),
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.resolved-conversations-header-title',
        ),
        gapAfter: true,
      },
      denominator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-total-volume',
        ),
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-total-support-volume',
        ),
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.total-support-volume-header-title',
        ),
        barColorClass: 'bg-neutral-border-emphasis',
      },
    };
  }

  get resolutionObjects() {
    return {
      numerator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-resolved',
        ),
        barColorClass: 'bg-vis-mint-40',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-resolved-conversations',
        ),
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.involved-conversations-header-title',
        ),
        gapAfter: true,
      },
      denominator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-involved',
        ),
        barColorClass: 'bg-vis-mint-10',
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.involved-conversations-header-title',
        ),
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-involved-conversations',
        ),
      },
    };
  }

  get involvementObjects() {
    return {
      numerator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-involved',
        ),
        barColorClass: 'bg-vis-mint-10',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-involved-conversations',
        ),
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.involved-conversations-header-title',
        ),
        gapAfter: true,
      },
      denominator: {
        label: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.label-total-volume',
        ),
        barColorClass: 'bg-neutral-border-emphasis',
        tooltipText: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.tooltip-total-support-volume',
        ),
        headerTitle: this.intl.t(
          'components.reporting.bespoke.fin-performance.automation-rate-section.total-support-volume-header-title',
        ),
      },
    };
  }

  get automationRateChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [{ metric_id: this.automationRateMetricId }],
    };
  }

  get resolutionRateChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [{ metric_id: this.resolutionRateMetricId }],
    };
  }

  get involvementRateChartDefinition() {
    return {
      visualization_type: 'counter',
      chart_series: [{ metric_id: this.participationRateMetricId }],
    };
  }

  get isZendeskOrSalesforceStandalone(): boolean {
    return (
      this.appService.app.isStandaloneApp &&
      (this.appService.app.hasStandalonePlatform('zendesk') ||
        this.appService.app.hasStandalonePlatform('salesforce'))
    );
  }

  get isHistoricalSyncingEnabled(): boolean {
    return this.appService.mainStandalonePlatform?.historicalSyncingEnabled ?? false;
  }

  get shouldShowTicketSyncWarningBanner(): boolean {
    return this.isZendeskOrSalesforceStandalone && !this.isHistoricalSyncingEnabled;
  }

  get standalonePlatformName(): 'zendesk' | 'salesforce' {
    if (this.appService.app.hasStandalonePlatform('zendesk')) {
      return 'zendesk';
    }
    return 'salesforce';
  }

  @action
  redirectToOptimize() {
    if (this.appService.app.isStandaloneApp) {
      safeWindowOpen(this.router.urlFor('apps.app.standalone.optimize-suggestions'), '_blank');
    } else {
      safeWindowOpen(
        this.router.urlFor('apps.app.automation.fin-ai-agent.analyze.optimize-suggestions'),
        '_blank',
      );
    }
  }

  @action
  openStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = true;
  }

  @action
  closeStandaloneSideDrawer() {
    this.showStandaloneSideDrawer = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::AutomationRateSection': typeof AutomationRateSection;
    'reporting/bespoke/fin-performance/automation-rate-section': typeof AutomationRateSection;
  }
}
